import React, { useState, useEffect } from 'react';
import { FaShieldAlt, FaUsers, FaEye, FaCheckCircle, FaTimes, FaPlus, FaEdit, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import RoleService, { permissionsToArray } from '../../services/RoleService';
import { canManageRoles } from '../../utils/permissions';
import RoleModal from './RoleModal';

const SaasRoleManagementSection = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const iconMap = {
    admin: FaShieldAlt,
    support: FaUsers,
    viewer: FaEye
  };

  const iconColorMap = {
    admin: 'text-blue-600',
    support: 'text-purple-600',
    viewer: 'text-gray-600'
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      
      // Check if RoleService is properly imported
      if (!RoleService || typeof RoleService.getRoles !== 'function') {
        throw new Error(`RoleService not properly imported. Type: ${typeof RoleService}, getRoles: ${typeof RoleService?.getRoles}`);
      }
      
      const data = await RoleService.getRoles();
      setRoles(data);
      setError('');
    } catch (err) {
      console.error('Error fetching roles:', err);
      
     
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setCurrentUser(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    fetchRoles();
  }, []);

  const handleCreateRole = () => {
    setEditingRole(null);
    setModalOpen(true);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setModalOpen(true);
  };

  const handleDeleteRole = async (role) => {
    try {
      await RoleService.deleteRole(role.id);
      await fetchRoles();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete role');
      console.error('Error deleting role:', err);
    }
  };

  const handleModalSuccess = () => {
    fetchRoles();
  };

  const getIconComponent = (roleName) => {
    return iconMap[roleName.toLowerCase()] || FaEye;
  };

  const getIconColor = (roleName) => {
    return iconColorMap[roleName.toLowerCase()] || 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Role Management</h1>
        {canManageRoles(currentUser) && (
          <button
            onClick={handleCreateRole}
            className="flex items-center px-4 py-2 bg-[#DB4F40] text-white rounded-md hover:bg-[#C64537] transition-all duration-200"
          >
            <FaPlus className="w-4 h-4 mr-2" />
            Add Role
          </button>
        )}
      </div>

      {/* Info about system roles */}
      <div className="mb-6 p-4 bg-[#FFEEED] border border-[#FFD6D4] rounded-md">
        <div className="flex items-start">
          <FaShieldAlt className="w-5 h-5 text-[#DB4F40] mr-2 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-[#DB4F40] mb-1">System Roles</h3>
            <p className="text-sm text-[#1B222A]/80">
              Admin, Support, and Viewer roles are system-wide and cannot be edited or deleted. 
              You can create custom roles for your organization with specific permissions.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center mb-2">
            <FaExclamationTriangle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 font-medium">Error Loading Roles</span>
          </div>
          <p className="text-red-700 mb-3">{error}</p>
          
          {/* Debug information */}
          <details className="text-sm">
            <summary className="cursor-pointer text-red-600 hover:text-red-800 mb-2">
              Debug Information
            </summary>
            <div className="bg-red-100 p-3 rounded text-xs">
              <p><strong>Token:</strong> {localStorage.getItem('token') ? 'Present' : 'Missing'}</p>
              <p><strong>API URL:</strong> {process.env.REACT_APP_API_URL || 'http://localhost:8181'}</p>
              <p><strong>Current Path:</strong> {window.location.pathname}</p>
              <button
                onClick={() => {
                  console.log('Debug info:', {
                    token: localStorage.getItem('token'),
                    apiUrl: process.env.REACT_APP_API_URL,
                    pathname: window.location.pathname
                  });
                }}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Log Debug Info to Console
              </button>
            </div>
          </details>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => {
          const IconComponent = getIconComponent(role.name);
          const permissions = permissionsToArray(role.permissions);
          const isSystemRole = role.customer_id === '00000000-0000-0000-0000-000000000000' || 
                              ['admin', 'support', 'viewer'].includes(role.name.toLowerCase());
          
          return (
            <div key={role.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <IconComponent className={`w-6 h-6 ${getIconColor(role.name)} mr-2`} />
                  <h3 className="text-lg font-semibold text-gray-900">{role.name}</h3>
                  {isSystemRole && (
                    <span className="ml-2 px-2 py-1 text-xs bg-[#FFD6D4] text-[#DB4F40] rounded-full">
                      System
                    </span>
                  )}
                </div>
                {!isSystemRole && canManageRoles(currentUser) && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditRole(role)}
                      className="p-1 text-gray-400 hover:text-[#DB4F40]"
                      title="Edit role"
                    >
                      <FaEdit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(role)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete role"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {!isSystemRole && !canManageRoles(currentUser) && (
                  <span className="text-gray-400 text-sm">View only</span>
                )}
              </div>
              
              <p className="text-gray-600 text-sm mb-4">
                {role.description || 'No description provided'}
              </p>
              
              <div className="space-y-2">
                {permissions.map((permission, index) => (
                  <div key={index} className="flex items-center text-sm">
                    {permission.granted ? (
                      <FaCheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    ) : (
                      <FaTimes className="w-4 h-4 text-red-500 mr-2" />
                    )}
                    <span>{permission.name}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Modal */}
      <RoleModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        role={editingRole}
        onSuccess={handleModalSuccess}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <FaExclamationTriangle className="w-6 h-6 text-red-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Delete Role</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the role "{deleteConfirm.name}"? This action cannot be undone.
            </p>
            {deleteConfirm.customer_id === '00000000-0000-0000-0000-000000000000' && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-yellow-800 text-sm">
                  <strong>Warning:</strong> This is a system role. Deleting it may affect system functionality.
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteRole(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaasRoleManagementSection;